import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface SubscriptionData {
  plan: 'free' | 'premium';
  monthlyUsage: number;
  plantCount: number;
  canIdentify: boolean;
  canDiagnose: boolean;
  canAddPlant: boolean;
  subscriptionEnd: string | null;
}

const FREE_LIMITS = {
  monthlyAiUsage: 3,
  maxPlants: 3,
};

const fetchSubscriptionData = async (userId: string, forceRefresh = false): Promise<SubscriptionData> => {
  // Call check-subscription edge function (with optional force bypass)
  const { data: subData, error: subError } = await supabase.functions.invoke('check-subscription', {
    body: forceRefresh ? { force: true } : undefined
  });
  
  if (subError) {
    console.error('Error checking subscription:', subError);
  }

  // If not authenticated (expired session), return free defaults
  if (subData?.authenticated === false) {
    console.log('Session expired or not authenticated, using free defaults');
    return {
      plan: 'free',
      monthlyUsage: 0,
      plantCount: 0,
      canIdentify: true,
      canDiagnose: true,
      canAddPlant: true,
      subscriptionEnd: null,
    };
  }

  const plan = subData?.plan_type === 'premium' ? 'premium' : 'free';
  const subscriptionEnd = subData?.subscription_end || null;

  // Fetch usage and plant count in parallel
  const [usageResult, plantResult] = await Promise.all([
    supabase.rpc('get_monthly_ai_usage', { p_user_id: userId }),
    supabase.from('plant_identifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
  ]);

  if (usageResult.error) {
    console.error('Error getting usage:', usageResult.error);
  }
  if (plantResult.error) {
    console.error('Error getting plant count:', plantResult.error);
  }

  const monthlyUsage = usageResult.data || 0;
  const plantCount = plantResult.count || 0;

  // Calculate permissions
  const isPremium = plan === 'premium';
  const canIdentify = isPremium || monthlyUsage < FREE_LIMITS.monthlyAiUsage;
  const canDiagnose = isPremium || monthlyUsage < FREE_LIMITS.monthlyAiUsage;
  const canAddPlant = isPremium || plantCount < FREE_LIMITS.maxPlants;

  return {
    plan,
    monthlyUsage,
    plantCount,
    canIdentify,
    canDiagnose,
    canAddPlant,
    subscriptionEnd,
  };
};

export function useSubscription() {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: () => fetchSubscriptionData(user!.id),
    enabled: !!user && !!session,
    staleTime: 5 * 60 * 1000, // 5 minutes - don't refetch while fresh
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const logUsage = async (usageType: 'identification' | 'diagnostic') => {
    if (!user) return;

    const { error } = await supabase
      .from('ai_usage_logs')
      .insert({
        user_id: user.id,
        usage_type: usageType,
      });

    if (error) {
      console.error('Error logging usage:', error);
    }

    // Invalidate cache to refetch updated usage
    queryClient.invalidateQueries({ queryKey: ['subscription', user.id] });
  };

  const refreshSubscription = async () => {
    if (!user) return;
    
    // Force refresh from Stripe (bypasses backend cache)
    await fetchSubscriptionData(user.id, true);
    
    // Invalidate React Query cache to refetch
    queryClient.invalidateQueries({ queryKey: ['subscription', user.id] });
  };

  return {
    plan: data?.plan ?? 'free',
    monthlyUsage: data?.monthlyUsage ?? 0,
    plantCount: data?.plantCount ?? 0,
    canIdentify: data?.canIdentify ?? true,
    canDiagnose: data?.canDiagnose ?? true,
    canAddPlant: data?.canAddPlant ?? true,
    subscriptionEnd: data?.subscriptionEnd ?? null,
    loading: isLoading,
    logUsage,
    refreshSubscription,
    limits: FREE_LIMITS,
  };
}
